import { z } from 'zod'

const uuid = z.string().uuid()
const optionalUuid = z
  .string()
  .uuid()
  .optional()
  .or(z.literal('').transform(() => undefined))
const optionalText = (max: number) =>
  z
    .string()
    .max(max)
    .optional()
    .or(z.literal('').transform(() => undefined))

const dateStr = z
  .string()
  .optional()
  .or(z.literal('').transform(() => undefined))

export const FRENTE_VALUES = [
  'GERAL',
  'CAR',
  'BIKE',
  'TRUCK',
  'OHW',
  'KONFORT',
  'MARINE',
  'TEXAEDU',
] as const

const stageEnum = z.enum(['pendente', 'em_andamento', 'concluido']).nullable().optional()

// --- Conteúdo / Tarefa (formulário enxuto v2) ------------------------------
export const contentSchema = z.object({
  is_content: z.boolean().default(true),
  title: z.string().min(1, 'Informe um título').max(200),
  frente: z.enum(FRENTE_VALUES),
  prioridade: z.enum(['critica', 'alta', 'planejada', 'backlog']),
  status: z
    .enum([
      'entrada',
      'aguardando_briefing',
      'pronto_para_produzir',
      'em_producao',
      'aguardando_material',
      'aguardando_aprovacao',
      'em_ajustes',
      'agendado',
      'publicado',
      'backlog_editorial',
    ])
    .optional(),
  etapa: z
    .enum(['ideia', 'roteiro', 'gravacao', 'edicao', 'aprovacao', 'agendado', 'publicado'])
    .optional(),
  formato: optionalText(60),
  canais: z.array(z.string()).default([]),
  apresentador: optionalText(120),
  revisor: optionalText(120),
  campaign_id: optionalUuid,
  event_id: optionalUuid,
  assignee_id: optionalUuid,
  stage_roteiro: stageEnum,
  stage_gravacao: stageEnum,
  stage_postagem: stageEnum,
  data_roteiro: dateStr,
  data_gravacao: dateStr,
  data_postagem: dateStr,
  horario: optionalText(8),
  next_action: optionalText(500),
  description: optionalText(8000),
})
export type ContentInput = z.infer<typeof contentSchema>

// --- Auth ------------------------------------------------------------------
export const signInSchema = z.object({
  email: z.string().email('E-mail inválido'),
  password: z.string().min(6, 'Mínimo de 6 caracteres'),
})
export type SignInInput = z.infer<typeof signInSchema>

export const resetRequestSchema = z.object({
  email: z.string().email('E-mail inválido'),
})
export type ResetRequestInput = z.infer<typeof resetRequestSchema>

export const updatePasswordSchema = z
  .object({
    password: z.string().min(8, 'Mínimo de 8 caracteres'),
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, {
    message: 'As senhas não coincidem',
    path: ['confirm'],
  })
export type UpdatePasswordInput = z.infer<typeof updatePasswordSchema>

// --- Workspace / convites --------------------------------------------------
export const workspaceSchema = z.object({
  name: z.string().min(1, 'Informe um nome').max(120),
  slug: z
    .string()
    .min(2)
    .max(48)
    .regex(/^[a-z0-9-]+$/, 'Use apenas letras minúsculas, números e hífen'),
  description: optionalText(500),
})
export type WorkspaceInput = z.infer<typeof workspaceSchema>

export const inviteSchema = z.object({
  email: z.string().email('E-mail inválido'),
  role: z.enum(['admin', 'gestor', 'colaborador', 'aprovador', 'visualizador']),
})
export type InviteInput = z.infer<typeof inviteSchema>

// --- Tarefa (o formulário central) -----------------------------------------
export const taskSchema = z
  .object({
    title: z.string().min(1, 'Informe um título').max(200),
    description: optionalText(5000),
    frente: z.enum(FRENTE_VALUES),
    tipo: z.enum([
      'planejamento',
      'organico',
      'campanha',
      'evento',
      'youtube',
      'copy_revisao',
      'landing_page',
      'video',
      'carrossel',
      'post_estatico',
    ]),
    pilar: z
      .enum([
        'educacao_tecnica',
        'produto',
        'aplicacao',
        'duvida',
        'bastidor',
        'evento',
        'caso',
        'parceiro',
      ])
      .optional()
      .or(z.literal('').transform(() => undefined)),
    status: z.enum([
      'entrada',
      'aguardando_briefing',
      'pronto_para_produzir',
      'em_producao',
      'aguardando_material',
      'aguardando_aprovacao',
      'em_ajustes',
      'agendado',
      'publicado',
      'backlog_editorial',
    ]),
    prioridade: z.enum(['critica', 'alta', 'planejada', 'backlog']),
    requester_id: optionalUuid,
    assignee_id: optionalUuid,
    tech_lead_id: optionalUuid,
    approver_id: optionalUuid,
    audience: optionalText(500),
    channel: optionalText(120),
    format: optionalText(120),
    start_date: dateStr,
    requested_due_date: dateStr,
    agreed_due_date: dateStr,
    publish_date: dateStr,
    next_action: optionalText(500),
    is_blocked: z.boolean().default(false),
    block_reason: optionalText(500),
    critical_justification: optionalText(1000),
    critical_affected_task_id: optionalUuid,
    campaign_id: optionalUuid,
    event_id: optionalUuid,
  })
  .refine((v) => !v.is_blocked || (v.block_reason && v.block_reason.length > 0), {
    message: 'Bloqueio exige um motivo',
    path: ['block_reason'],
  })
  .refine(
    (v) =>
      v.prioridade !== 'critica' ||
      (v.critical_justification && v.critical_justification.length > 0),
    {
      message: 'Prioridade crítica exige justificativa',
      path: ['critical_justification'],
    },
  )
export type TaskInput = z.infer<typeof taskSchema>

// --- Comentário ------------------------------------------------------------
export const commentSchema = z.object({
  body: z.string().min(1, 'Escreva algo').max(5000),
})
export type CommentInput = z.infer<typeof commentSchema>

// --- Campanha / Evento -----------------------------------------------------
export const campaignSchema = z.object({
  name: z.string().min(1).max(160),
  objective: optionalText(1000),
  audience: optionalText(1000),
  central_message: optionalText(1000),
  offer: optionalText(1000),
  frente: z.enum(FRENTE_VALUES),
  status: z.enum(['planejamento', 'ativa', 'pausada', 'concluida', 'cancelada']),
  landing_page_url: z
    .string()
    .url('URL inválida')
    .optional()
    .or(z.literal('').transform(() => undefined)),
  start_date: dateStr,
  end_date: dateStr,
  owner_id: optionalUuid,
})
export type CampaignInput = z.infer<typeof campaignSchema>

export const eventSchema = z.object({
  name: z.string().min(1).max(160),
  description: optionalText(1000),
  location: optionalText(200),
  frente: z.enum(FRENTE_VALUES),
  status: z.enum(['planejamento', 'confirmado', 'em_andamento', 'concluido', 'cancelado']),
  start_at: dateStr,
  end_at: dateStr,
  owner_id: optionalUuid,
})
export type EventInput = z.infer<typeof eventSchema>

// --- Calendário ------------------------------------------------------------
export const calendarSchema = z.object({
  title: z.string().min(1).max(200),
  channel: z.enum([
    'instagram_feed',
    'instagram_stories',
    'youtube',
    'tiktok',
    'linkedin',
    'facebook',
    'blog',
    'campanha',
    'evento',
  ]),
  frente: z.enum(FRENTE_VALUES),
  scheduled_at: z.string().min(1, 'Informe data e hora'),
  task_id: optionalUuid,
  campaign_id: optionalUuid,
  notes: optionalText(1000),
})
export type CalendarInput = z.infer<typeof calendarSchema>

// --- Rótulo ----------------------------------------------------------------
export const labelSchema = z.object({
  name: z.string().min(1).max(60),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Cor inválida'),
})
export type LabelInput = z.infer<typeof labelSchema>

export { uuid }
